import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  ScanLine, Loader2, Lock, LogOut, Trash2, Plus, Minus, UserPlus, X, Ban,
  Pause, Play, Receipt, RotateCcw, Wallet, Tag, Delete, Camera, Banknote, CheckCircle2, Printer,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandMark from "@/components/common/BrandMark";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { StepUpDialog } from "@/components/pos/StepUpDialog";
import { PaymentDialog } from "@/components/pos/PaymentDialog";
import { ReturnDialog } from "@/components/pos/ReturnDialog";
import { ReceiptDialog } from "@/components/pos/ReceiptDialog";
import { CameraBarcodeScanner } from "@/components/common/CameraBarcodeScanner";
import { printDoc } from "@/lib/downloadDoc";

const IDLE_MS = 120000;
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const digitKeys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0", "back"];

function PinPad({ value, onChange, disabled = false }) {
  const press = (key) => {
    if (disabled) return;
    if (key === "clear") onChange("");
    else if (key === "back") onChange(value.slice(0, -1));
    else if (value.length < 8) onChange(`${value}${key}`);
  };
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="pos-pin-pad">
      {digitKeys.map((key) => (
        <Button key={key} type="button" variant="outline" disabled={disabled}
          className="h-12 border-slate-700 bg-slate-800 text-lg text-white hover:bg-slate-700"
          onClick={() => press(key)} data-testid={`pos-pin-${key}`}>
          {key === "clear" ? "C" : key === "back" ? <Delete className="h-5 w-5" /> : key}
        </Button>
      ))}
    </div>
  );
}

/* ---------------- One-time station pairing ---------------- */
function PairStation({ onPaired }) {
  const { t } = useI18n();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/pos/station/pair", { code: code.trim() }, { _skipAuthRedirect: true });
      onPaired();
    }
    catch (err) { toast.error(apiErrorMessage(err, t)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 p-6" data-testid="pos-pair">
      <form onSubmit={submit} className="w-full max-w-sm space-y-5 rounded-2xl bg-slate-900 p-8 ring-1 ring-slate-800">
        <div className="flex items-center gap-3">
          <BrandMark className="h-14 w-14" />
          <div>
            <div className="font-bold text-white">{t("pos.station")}</div>
            <div className="text-xs text-slate-400">Mayr-Technologies</div>
          </div>
        </div>
        <div>
          <h1 className="text-xl font-bold text-white">{t("pos.pair_title")}</h1>
          <p className="text-sm text-slate-400">{t("pos.pair_subtitle")}</p>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{t("pos.pair_code")}</Label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} autoFocus autoComplete="off"
            className="bg-slate-800 font-mono text-white" data-testid="pos-pair-code" />
        </div>
        <Button type="submit" className="h-12 w-full bg-emerald-500 text-slate-950 hover:bg-emerald-400"
          disabled={busy || !code.trim()} data-testid="pos-pair-submit">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("pos.pair_action")}
        </Button>
      </form>
    </div>
  );
}

/* ---------------- Employee + PIN login ---------------- */
function CashierLogin({ station, onAuthenticated }) {
  const { t } = useI18n();
  const [registerId, setRegisterId] = useState(station.registers?.[0]?.id || "");
  const [employees, setEmployees] = useState([]);
  const [employeeCode, setEmployeeCode] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!registerId) return;
    setEmployeeCode(""); setPin("");
    api.get(`/pos/station/employees?register_id=${encodeURIComponent(registerId)}`, { _skipAuthRedirect: true })
      .then(({ data }) => setEmployees(data))
      .catch((e) => toast.error(apiErrorMessage(e, t)));
  }, [registerId, t]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        "/pos/cashier/login", { register_id: registerId, employee_code: employeeCode, pin },
        { _skipAuthRedirect: true },
      );
      setPin("");
      onAuthenticated(data);
    } catch (e) {
      setPin("");
      toast.error(e?.response?.status === 423 ? t("pos.pin_locked") : t("pos.invalid_pin"));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 p-4" data-testid="pos-cashier-login">
      <Card className="w-full max-w-md space-y-5 border-slate-800 bg-slate-900 p-6 text-white">
        <div>
          <div className="text-sm text-emerald-400">{station.label}</div>
          <h1 className="text-xl font-bold">{t("pos.cashier_login_title")}</h1>
          <p className="text-sm text-slate-400">{t("pos.cashier_login_subtitle")}</p>
        </div>
        <div className="space-y-1.5">
          <Label>{t("pos.register")}</Label>
          <Select value={registerId} onValueChange={setRegisterId}>
            <SelectTrigger className="h-11 bg-slate-800" data-testid="pos-login-register"><SelectValue /></SelectTrigger>
            <SelectContent>{station.registers.map((r) => <SelectItem key={r.id} value={r.id}>{r.code} — {r.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{t("pos.employee")}</Label>
          <Select value={employeeCode} onValueChange={(v) => { setEmployeeCode(v); setPin(""); }}>
            <SelectTrigger className="h-11 bg-slate-800" data-testid="pos-login-employee"><SelectValue placeholder={t("pos.select_employee")} /></SelectTrigger>
            <SelectContent>{employees.map((employee) => (
              <SelectItem key={employee.membership_id} value={employee.employee_code}>{employee.name}</SelectItem>
            ))}</SelectContent>
          </Select>
        </div>
        <div className="rounded-lg bg-slate-950 p-3 text-center font-mono text-xl tracking-[0.45em]" data-testid="pos-pin-display">
          {pin ? "•".repeat(pin.length) : "—"}
        </div>
        <PinPad value={pin} onChange={setPin} disabled={busy || !employeeCode} />
        <Button className="h-12 w-full bg-emerald-500 font-bold text-slate-950 hover:bg-emerald-400"
          disabled={busy || !employeeCode || pin.length < 4} onClick={submit} data-testid="pos-pin-submit">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("pos.unlock")}
        </Button>
      </Card>
    </div>
  );
}

/* ---------------- Lock overlay ---------------- */
function LockOverlay({ cashier, onUnlock, onLogout }) {
  const { t } = useI18n();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const unlock = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pos/cashier/login", {
        register_id: cashier.register.id, employee_code: cashier.employee_code, pin,
      }, { _skipAuthRedirect: true });
      setPin(""); onUnlock(data);
    } catch (e) {
      setPin("");
      toast.error(e?.response?.status === 423 ? t("pos.pin_locked") : t("pos.invalid_pin"));
    }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/95 backdrop-blur" data-testid="pos-lock">
      <div className="w-full max-w-sm space-y-4 rounded-2xl bg-slate-900 p-8 text-center ring-1 ring-slate-800">
        <Lock className="mx-auto h-10 w-10 text-slate-400" />
        <h1 className="text-lg font-bold text-white">{t("pos.locked_title")}</h1>
        <p className="text-sm text-slate-400">{t("pos.locked_body")}</p>
        <div className="text-sm text-slate-300">{cashier.cashier_name}</div>
        <div className="rounded-lg bg-slate-950 p-3 font-mono text-xl tracking-[0.45em]">{pin ? "•".repeat(pin.length) : "—"}</div>
        <PinPad value={pin} onChange={setPin} disabled={busy} />
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onLogout} data-testid="pos-lock-logout">{t("pos.exit")}</Button>
          <Button className="flex-1 bg-emerald-500 text-slate-950 hover:bg-emerald-400" disabled={busy || pin.length < 4} onClick={unlock} data-testid="pos-lock-unlock">{t("pos.unlock")}</Button>
        </div>
      </div>
    </div>
  );
}

function CashBookDialog({ open, onOpenChange, session, onRefresh, guardWithStepUp }) {
  const { t, fmtCurrency } = useI18n();
  const [mode, setMode] = useState("invoice"); const [q, setQ] = useState(""); const [invoices, setInvoices] = useState([]); const [invoice, setInvoice] = useState(null);
  const [amount, setAmount] = useState(""); const [note, setNote] = useState(""); const [reference, setReference] = useState(""); const [busy, setBusy] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [paidInvoice, setPaidInvoice] = useState(null);
  const [idempotencyKey, setIdempotencyKey] = useState(uid);
  useEffect(() => { if (!open || mode !== "invoice") return; const timer = setTimeout(() => api.get("/pos/cashbook/open-invoices", { params: { q } }).then(({ data }) => setInvoices(data)).catch(() => setInvoices([])), 150); return () => clearTimeout(timer); }, [open, mode, q]);
  const clearEntry = () => { setInvoice(null); setAmount(""); setNote(""); setReference(""); setReceiptFile(null); setIdempotencyKey(uid()); setQ(""); };
  const reset = () => { clearEntry(); setPaidInvoice(null); };
  const payInvoice = async () => { setBusy(true); try { const selected = invoice; const { data } = await api.post("/pos/cashbook/invoice-payment", { session_id: session.id, einvoice_id: selected.id, amount, note, idempotency_key: idempotencyKey }); onRefresh(); if (data.paid_in_full) { clearEntry(); setPaidInvoice({ id: selected.id, number: selected.number, customer_name: selected.customer_name, amount: data.amount }); } else { toast.success(t("pos.invoice_paid_cash")); reset(); onOpenChange(false); } } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusy(false); } };
  const movement = () => guardWithStepUp("pos:override", async () => { setBusy(true); try { const { data } = await api.post("/pos/cashbook/movements", { session_id: session.id, movement_type: mode, amount, note, reference, receipt_reference: reference, idempotency_key: idempotencyKey }); if (receiptFile) { const fd = new FormData(); fd.append("file", receiptFile); await api.post(`/pos/cashbook/movements/${data.id}/receipt`, fd); } toast.success(t("pos.cash_movement_saved")); reset(); onOpenChange(false); onRefresh(); } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusy(false); } });
  return <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}><DialogContent className="border-slate-800 bg-slate-900 text-slate-100 sm:max-w-2xl"><DialogHeader><DialogTitle>{t("pos.cashbook")}</DialogTitle><DialogDescription>{paidInvoice ? t("pos.payment_complete_hint") : t("pos.cashbook_hint")}</DialogDescription></DialogHeader>
    {paidInvoice ? <div className="rounded-xl border border-emerald-600 bg-emerald-950/40 p-6 text-center"><CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-emerald-400" /><h3 className="text-xl font-bold text-emerald-300">{t("pos.payment_complete")}</h3><p className="mt-2 text-slate-300">{paidInvoice.number} · {paidInvoice.customer_name}</p><p className="mt-1 text-2xl font-bold">{fmtCurrency(paidInvoice.amount)}</p></div> : <><div className="grid grid-cols-4 gap-2">{["invoice", "expense", "withdrawal", "deposit"].map((x) => <Button key={x} variant={mode === x ? "default" : "outline"} onClick={() => { setMode(x); reset(); }}>{t(`pos.cash_${x}`)}</Button>)}</div>
      {mode === "invoice" ? <div className="space-y-3"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("pos.search_open_invoice")} className="bg-slate-800" />{!invoice ? <div className="max-h-64 space-y-2 overflow-y-auto">{invoices.map((x) => <button key={x.id} onClick={() => { setInvoice(x); setAmount(x.open_amount); }} className="flex w-full items-center justify-between rounded-md border border-slate-700 p-3 text-left"><span><b>{x.number}</b><div className="text-xs text-slate-400">{x.customer_name}</div></span><b>{fmtCurrency(x.open_amount)}</b></button>)}</div> : <div className="rounded-md border border-emerald-700 bg-emerald-950/30 p-3"><div className="flex justify-between"><span>{invoice.number} · {invoice.customer_name}</span><b>{fmtCurrency(invoice.open_amount)}</b></div></div>}</div> : <div className="rounded-md border border-amber-700 bg-amber-950/20 p-3 text-sm text-amber-200">{t("pos.manager_cash_hint")}</div>}
      {(invoice || mode !== "invoice") && <div className="grid gap-3 sm:grid-cols-2"><div><Label>{t("banking.amount")}</Label><Input type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-slate-800" /></div>{mode !== "invoice" && <div><Label>{t("pos.receipt_reference")}</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} className="bg-slate-800" placeholder={t("pos.receipt_reference_hint")} /></div>}<div className="sm:col-span-2"><Label>{t("common.notes")} *</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="bg-slate-800" /></div>{mode !== "invoice" && <div className="sm:col-span-2"><Label>{t("pos.receipt_upload")}</Label><Input type="file" accept="application/pdf,image/png,image/jpeg" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} className="bg-slate-800" /><p className="mt-1 text-xs text-slate-400">{t("pos.receipt_upload_hint")}</p></div>}</div>}</>}
    <DialogFooter>{paidInvoice ? <><Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>{t("pos.done")}</Button><Button onClick={() => printDoc(`/einvoice/invoices/${paidInvoice.id}/print-copy?stationery=standard`)}><Printer className="h-4 w-4" />{t("pos.print_paid_invoice")}</Button></> : <><Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button><Button onClick={mode === "invoice" ? payInvoice : movement} disabled={busy || !amount || (mode === "invoice" ? !invoice : !note.trim())}>{t("pos.book_cash")}</Button></>}</DialogFooter></DialogContent></Dialog>;
}

/* ---------------- Customer search ---------------- */
function CustomerSearch({ value, onSelect, onClear }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(async () => {
      const { data } = await api.get(`/pos/customers?q=${encodeURIComponent(q)}`);
      setResults(data);
    }, 200);
    return () => clearTimeout(id);
  }, [q, open]);

  if (value) {
    return (
      <div className="flex items-center justify-between rounded-md bg-slate-800 px-3 py-2 text-sm text-white" data-testid="pos-customer-selected">
        <span className="truncate">{value.name}</span>
        <button onClick={onClear} data-testid="pos-customer-clear"><X className="h-4 w-4" /></button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Button variant="outline" className="w-full justify-start bg-slate-800 text-slate-300" onClick={() => setOpen((o) => !o)} data-testid="pos-customer-open">
        <UserPlus className="mr-2 h-4 w-4" />{t("pos.walk_in")}
      </Button>
      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-md bg-slate-800 p-2 shadow-xl ring-1 ring-slate-700" data-testid="pos-customer-dropdown">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("pos.search_customer")} autoFocus className="mb-2 bg-slate-900 text-white" data-testid="pos-customer-search" />
          <div className="max-h-48 overflow-y-auto">
            {results.map((c) => (
              <button key={c.id} onClick={() => { onSelect(c); setOpen(false); setQ(""); }}
                className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm text-slate-200 hover:bg-slate-700" data-testid={`pos-customer-option-${c.id}`}>
                <span className="truncate">{c.name}</span><span className="text-xs text-slate-500">{c.customer_number}</span>
              </button>
            ))}
            {results.length === 0 && <div className="px-2 py-3 text-center text-xs text-slate-500">—</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Main station ---------------- */
export default function PosStation() {
  const { t, fmtCurrency } = useI18n();
  const [stationState, setStationState] = useState("loading");
  const [station, setStation] = useState(null);
  const [cashier, setCashier] = useState(null);

  const [registers, setRegisters] = useState([]);
  const [registerId, setRegisterId] = useState("");
  const [session, setSession] = useState(null);
  const [openingFloat, setOpeningFloat] = useState("0");
  const [sale, setSale] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [scanValue, setScanValue] = useState("");
  const [qty, setQty] = useState("1");
  const [busy, setBusy] = useState(false);
  const [locked, setLocked] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnSale, setReturnSale] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [heldOpen, setHeldOpen] = useState(false);
  const [held, setHeld] = useState([]);
  const [recentOpen, setRecentOpen] = useState(false);
  const [recent, setRecent] = useState([]);
  const [closeOpen, setCloseOpen] = useState(false);
  const [countedCash, setCountedCash] = useState("");
  const [varReason, setVarReason] = useState("");
  const [overrideLine, setOverrideLine] = useState(null);
  const [stepUp, setStepUp] = useState(null); // { actions, onToken }
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cashbookOpen, setCashbookOpen] = useState(false);

  const scanRef = useRef(null);
  const lastScan = useRef({ code: "", ts: 0 });
  const stepUpToken = useRef(null); // in-memory short-lived elevation

  const stepUpHeaders = () => (stepUpToken.current ? { "X-POS-StepUp": stepUpToken.current } : {});
  const can = useCallback((permission) => (cashier?.permissions || []).includes(permission), [cashier]);

  const loadStation = useCallback(async () => {
    setStationState("loading");
    try {
      const { data } = await api.get("/pos/cashier/me", { _skipAuthRedirect: true });
      setCashier(data);
      setStation({ label: data.register.name, registers: [data.register] });
      setRegisterId(data.register.id);
      setStationState("authenticated");
      return;
    } catch (e) {
      // No active cashier is normal after pairing, locking out or ending a shift.
    }
    try {
      const { data } = await api.get("/pos/station/registers", { _skipAuthRedirect: true });
      setStation(data);
      setRegisterId(data.registers?.[0]?.id || "");
      setCashier(null);
      setStationState("paired");
    } catch (e) {
      setStation(null);
      setCashier(null);
      setStationState("unpaired");
    }
  }, []);

  useEffect(() => { loadStation(); }, [loadStation]);

  const cashierLogout = useCallback(async () => {
    try { await api.post("/pos/cashier/logout", {}, { _skipAuthRedirect: true }); } catch (e) { /* local reset still applies */ }
    setLocked(false);
    setSale(null);
    setSession(null);
    setCashier(null);
    await loadStation();
  }, [loadStation]);

  /* clear any manager elevation when the station locks */
  useEffect(() => { if (locked) stepUpToken.current = null; }, [locked]);

  /* inactivity lock */
  useEffect(() => {
    if (stationState !== "authenticated") return undefined;
    let timer;
    const reset = () => { clearTimeout(timer); timer = setTimeout(() => setLocked(true), IDLE_MS); };
    reset();
    const evts = ["keydown", "mousedown", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, reset));
    return () => { clearTimeout(timer); evts.forEach((e) => window.removeEventListener(e, reset)); };
  }, [stationState]);

  const loadRegisters = useCallback(async () => {
    const { data } = await api.get("/pos/registers");
    setRegisters(data);
    if (data[0]) setRegisterId((r) => r || data[0].id);
  }, []);

  useEffect(() => { if (stationState === "authenticated") loadRegisters(); }, [stationState, loadRegisters]);

  const refreshSession = useCallback(async (rid) => {
    if (!rid) return;
    const { data } = await api.get(`/pos/sessions/current?register_id=${rid}`);
    setSession(data.open ? data : null);
    if (!data.open) setOpeningFloat(data.suggested_opening_float || "0");
  }, []);

  useEffect(() => { if (registerId) refreshSession(registerId); }, [registerId, refreshSession]);

  const openSession = async () => {
    setBusy(true);
    try {
      await api.post("/pos/sessions/open", { register_id: registerId, opening_float: openingFloat || "0" });
      await refreshSession(registerId);
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
    finally { setBusy(false); }
  };

  const ensureSale = async () => {
    if (sale) return sale;
    const { data } = await api.post("/pos/sales", { session_id: session?.id, customer_id: customer?.id });
    setSale(data);
    return data;
  };

  const refreshSale = async (id) => { const { data } = await api.get(`/pos/sales/${id}`); setSale(data); return data; };

  const submitScan = async (raw) => {
    const code = (raw ?? scanValue).trim();
    if (!code) return;
    const now = Date.now();
    if (lastScan.current.code === code && now - lastScan.current.ts < 700) { setScanValue(""); return; }
    lastScan.current = { code, ts: now };
    setScanValue("");
    try {
      const s = await ensureSale();
      const { data } = await api.post(`/pos/sales/${s.id}/lines`, { code, quantity: qty || "1" });
      if (data.resolve && data.resolve.status === "not_found") { toast.error(`${t("scan.not_found")}: ${code}`); return; }
      if (data.resolve && data.resolve.status === "ambiguous") { toast.error(t("scan.ambiguous")); return; }
      setSale(data); setQty("1");
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
    finally { scanRef.current?.focus(); }
  };

  const changeQty = async (line, delta) => {
    const next = Number(line.quantity) + delta;
    if (next <= 0) { await removeLine(line.id); return; }
    const { data } = await api.patch(`/pos/sales/${sale.id}/lines/${line.id}`, { quantity: String(next) });
    setSale(data);
  };

  const removeLine = async (lid) => {
    await api.delete(`/pos/sales/${sale.id}/lines/${lid}`);
    await refreshSale(sale.id);
  };

  const setSaleCustomer = async (c) => {
    setCustomer(c);
    if (sale) { await refreshSale(sale.id); } // price re-eval happens per line on next add
  };

  const resetSale = () => { setSale(null); setCustomer(null); stepUpToken.current = null; setQty("1"); };

  /* ---- payment / complete ---- */
  const doComplete = async (payments) => {
    try {
      const { data } = await api.post(`/pos/sales/${sale.id}/complete`,
        { payments, idempotency_key: sale.id }, { headers: stepUpHeaders() });
      setPayOpen(false);
      const { data: rc } = await api.get(`/pos/sales/${sale.id}/receipt`);
      setReceipt(rc);
      toast.success(t("pos.sale_completed"));
      resetSale();
      refreshSession(registerId);
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };

  /* ---- hold / resume ---- */
  const holdSale = async () => {
    if (!sale) return;
    await api.post(`/pos/sales/${sale.id}/hold`, { hold_name: customer?.name || t("pos.cart") });
    resetSale();
    toast.success(t("pos.held"));
  };
  const openHeld = async () => { const { data } = await api.get("/pos/sales?held=true"); setHeld(data); setHeldOpen(true); };
  const resumeSale = async (s) => {
    await api.post(`/pos/sales/${s.id}/resume`);
    const full = await refreshSale(s.id);
    setSale(full); setHeldOpen(false);
  };

  /* ---- void (manager) ---- */
  const voidSale = () => {
    if (!sale) return;
    guardWithStepUp("pos:void", async () => {
      await api.post(`/pos/sales/${sale.id}/void`, {}, { headers: stepUpHeaders() });
      resetSale(); toast.success(t("pos.void"));
    });
  };

  /* ---- override price (manager) ---- */
  const applyOverride = (line, unitPrice, reason) => {
    guardWithStepUp("pos:override", async () => {
      try {
        // add the overridden line first (authorized); only remove the original if that succeeds
        await api.post(`/pos/sales/${sale.id}/lines`,
          { product_id: line.product_id, quantity: String(line.quantity), unit_price: unitPrice, override_reason: reason },
          { headers: stepUpHeaders() });
        await api.delete(`/pos/sales/${sale.id}/lines/${line.id}`);
        const full = await refreshSale(sale.id);
        setSale(full); setOverrideLine(null);
      } catch (e) {
        toast.error(apiErrorMessage(e, t));
        await refreshSale(sale.id);
      }
    });
  };

  /* ---- recent + returns ---- */
  const openRecent = async () => { const { data } = await api.get("/pos/sales?status=completed&limit=20"); setRecent(data); setRecentOpen(true); };
  const startReturn = (s) => { setReturnSale(s); setRecentOpen(false); setReturnOpen(true); };
  const doReturn = async (payload) => {
    try {
      const { data } = await api.post(`/pos/sales/${returnSale.id}/return`, payload, { headers: stepUpHeaders() });
      setReturnOpen(false);
      const { data: rc } = await api.get(`/pos/sales/${data.id}/receipt`);
      setReceipt(rc);
      toast.success(t("pos.return"));
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };

  /* ---- close session (manager) ---- */
  const confirmClose = () => {
    guardWithStepUp("pos:close", async () => {
      try {
        await api.post(`/pos/sessions/${session.id}/close`,
          { counted_cash: countedCash || "0", reason: varReason || undefined }, { headers: stepUpHeaders() });
        setCloseOpen(false); setCountedCash(""); setVarReason("");
        toast.success(t("pos.z_report"));
        await refreshSession(registerId);
      } catch (e) { toast.error(apiErrorMessage(e, t)); }
    });
  };

  /* step-up gate: if cashier lacks permission, prompt manager; else run directly */
  const guardWithStepUp = (action, fn) => {
    if (can(action)) { fn(); return; }
    setStepUp({
      actions: [action],
      onToken: (token) => { stepUpToken.current = token; fn(); },
    });
  };

  if (stationState === "loading") {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>;
  }
  if (stationState === "unpaired") return <PairStation onPaired={loadStation} />;
  if (stationState === "paired") return <CashierLogin station={station} onAuthenticated={(data) => {
    setCashier(data); setRegisterId(data.register.id); setStationState("authenticated");
  }} />;

  const lines = sale?.lines || [];
  const net = sale?.net_total || "0";
  const tax = sale?.tax_total || "0";
  const gross = sale?.gross_total || "0";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="pos-station">
      {/* top bar */}
      <div className="flex items-center justify-between border-b border-slate-800 px-4 py-2">
        <div className="flex items-center gap-3">
          <BrandMark className="h-8 w-8" />
          <span className="font-bold">{t("pos.station")}</span>
          {session && <Badge className="bg-emerald-600 hover:bg-emerald-600" data-testid="pos-session-badge">{t("pos.session_open")}</Badge>}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-400">{cashier?.cashier_name} · {cashier?.register?.code}</span>
          <Button variant="ghost" size="sm" onClick={() => setLocked(true)} data-testid="pos-lock-btn"><Lock className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" onClick={cashierLogout} data-testid="pos-logout-btn"><LogOut className="h-4 w-4" /></Button>
        </div>
      </div>

      {!session ? (
        /* ---- session gate ---- */
        <div className="flex min-h-[70vh] items-center justify-center p-6">
          <Card className="w-full max-w-md space-y-4 border-slate-800 bg-slate-900 p-6 text-slate-100" data-testid="pos-open-session">
            <h2 className="text-lg font-bold">{t("pos.open_session")}</h2>
            <p className="text-sm text-slate-400">{t("pos.open_session_hint")}</p>
            <div className="space-y-1.5">
              <Label>{t("pos.register")}</Label>
              <Select value={registerId} onValueChange={setRegisterId}>
                <SelectTrigger className="bg-slate-800" data-testid="pos-register-select"><SelectValue placeholder={t("pos.select_register")} /></SelectTrigger>
                <SelectContent>{registers.map((r) => <SelectItem key={r.id} value={r.id}>{r.code} — {r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("pos.opening_float")}</Label>
              <Input type="number" step="0.01" value={openingFloat} onChange={(e) => setOpeningFloat(e.target.value)} className="bg-slate-800 tabular-nums" data-testid="pos-opening-float" />
            </div>
            <Button className="h-11 w-full bg-emerald-500 text-slate-950 hover:bg-emerald-400" disabled={busy || !registerId} onClick={openSession} data-testid="pos-open-session-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wallet className="mr-2 h-4 w-4" />}{t("pos.open_session")}
            </Button>
          </Card>
        </div>
      ) : (
        /* ---- register grid ---- */
        <div className="grid gap-4 p-4 lg:grid-cols-[1fr_380px]">
          {/* left: scan + cart */}
          <div className="space-y-3">
            <Card className="border-slate-800 bg-slate-900 p-3 text-slate-100">
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                  <Label className="text-slate-300">{t("pos.scan_here")}</Label>
                  <Input ref={scanRef} autoFocus value={scanValue} onChange={(e) => setScanValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitScan(); } }}
                    className="h-12 bg-slate-800 text-lg tabular-nums" data-testid="pos-scan-input" />
                </div>
                <div className="w-20 space-y-1">
                  <Label className="text-slate-300">{t("pos.quantity")}</Label>
                  <Input type="number" step="1" value={qty} onChange={(e) => setQty(e.target.value)} className="h-12 bg-slate-800 tabular-nums" data-testid="pos-qty-input" />
                </div>
                <Button className="h-12 bg-slate-700 hover:bg-slate-600" onClick={() => submitScan()} data-testid="pos-add-btn"><ScanLine className="h-5 w-5" /></Button>
                <Button className="h-12 bg-emerald-600 hover:bg-emerald-500" onClick={() => setCameraOpen(true)} data-testid="pos-camera-btn">
                  <Camera className="h-5 w-5" /><span className="sr-only">{t("scan.use_camera")}</span>
                </Button>
              </div>
            </Card>

            <Card className="min-h-[50vh] border-slate-800 bg-slate-900 p-3 text-slate-100" data-testid="pos-cart">
              {lines.length === 0 ? (
                <div className="flex h-full min-h-[45vh] items-center justify-center text-center text-slate-500" data-testid="pos-cart-empty">{t("pos.empty_cart")}</div>
              ) : (
                <div className="space-y-2">
                  {lines.map((l) => (
                    <div key={l.id} className="flex items-center gap-3 rounded-lg bg-slate-800 p-2" data-testid="pos-cart-line">
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{l.description}</div>
                        <div className="truncate text-xs text-slate-400">
                          {l.sku} · {fmtCurrency(l.unit_price)}
                          {Number(l.discount_percent) > 0 && <span className="ml-1 text-amber-400">-{l.discount_percent}%</span>}
                          {l.price_source === "override" && <Badge variant="outline" className="ml-2 border-amber-500 text-amber-400">override</Badge>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => changeQty(l, -1)} data-testid={`pos-line-dec-${l.id}`}><Minus className="h-4 w-4" /></Button>
                        <span className="w-8 text-center tabular-nums" data-testid={`pos-line-qty-${l.id}`}>{Number(l.quantity)}</span>
                        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => changeQty(l, 1)} data-testid={`pos-line-inc-${l.id}`}><Plus className="h-4 w-4" /></Button>
                      </div>
                      <div className="w-24 text-right font-bold tabular-nums" data-testid={`pos-line-total-${l.id}`}>{fmtCurrency(l.line_gross)}</div>
                      <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setOverrideLine(l)} data-testid={`pos-line-override-${l.id}`}><Tag className="h-4 w-4 text-slate-400" /></Button>
                      <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => removeLine(l.id)} data-testid={`pos-line-remove-${l.id}`}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* right: totals + actions */}
          <div className="space-y-3">
            <Card className="border-slate-800 bg-slate-900 p-3 text-slate-100">
              <CustomerSearch value={customer} onSelect={setSaleCustomer} onClear={() => setSaleCustomer(null)} />
            </Card>

            <Card className="border-slate-800 bg-slate-900 p-4 text-slate-100">
              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-slate-400"><span>{t("pos.net")}</span><span className="tabular-nums" data-testid="pos-total-net">{fmtCurrency(net)}</span></div>
                <div className="flex justify-between text-slate-400"><span>{t("pos.tax")}</span><span className="tabular-nums" data-testid="pos-total-tax">{fmtCurrency(tax)}</span></div>
                <div className="mt-2 flex items-baseline justify-between border-t border-slate-800 pt-2">
                  <span className="text-lg font-bold">{t("pos.total")}</span>
                  <span className="text-3xl font-bold tabular-nums text-emerald-400" data-testid="pos-total-gross">{fmtCurrency(gross)}</span>
                </div>
              </div>
              <Button className="mt-3 h-14 w-full bg-emerald-500 text-lg font-bold text-slate-950 hover:bg-emerald-400"
                disabled={lines.length === 0} onClick={() => setPayOpen(true)} data-testid="pos-pay-btn">
                <Wallet className="mr-2 h-5 w-5" />{t("pos.pay")}
              </Button>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Button variant="outline" className="border-slate-700 bg-slate-800" disabled={lines.length === 0} onClick={holdSale} data-testid="pos-hold-btn"><Pause className="mr-1 h-4 w-4" />{t("pos.hold")}</Button>
                <Button variant="outline" className="border-slate-700 bg-slate-800" disabled={lines.length === 0} onClick={voidSale} data-testid="pos-void-btn"><Ban className="mr-1 h-4 w-4" />{t("pos.void")}</Button>
              </div>
            </Card>

            <Card className="grid grid-cols-2 gap-2 border-slate-800 bg-slate-900 p-3 text-slate-100">
              <Button variant="ghost" className="justify-start" onClick={openHeld} data-testid="pos-held-btn"><Play className="mr-2 h-4 w-4" />{t("pos.held_sales")}</Button>
              <Button variant="ghost" className="justify-start" onClick={openRecent} data-testid="pos-recent-btn"><Receipt className="mr-2 h-4 w-4" />{t("pos.recent_sales")}</Button>
              <Button variant="ghost" className="col-span-2 justify-start text-emerald-400" onClick={() => setCashbookOpen(true)}><Banknote className="mr-2 h-4 w-4" />{t("pos.cashbook")}</Button>
              <Button variant="ghost" className="col-span-2 justify-start text-red-400" onClick={() => setCloseOpen(true)} data-testid="pos-close-session-btn"><RotateCcw className="mr-2 h-4 w-4" />{t("pos.close_session")}</Button>
            </Card>
          </div>
        </div>
      )}

      {/* dialogs */}
      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} grossTotal={gross} onComplete={doComplete} />
      <ReturnDialog open={returnOpen} onOpenChange={setReturnOpen} sale={returnSale} canRestock={can("inventory:write")} onSubmit={doReturn} />
      <ReceiptDialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)} receipt={receipt} />
      <CameraBarcodeScanner open={cameraOpen} onOpenChange={setCameraOpen} onDetected={submitScan} dark />
      {session && <CashBookDialog open={cashbookOpen} onOpenChange={setCashbookOpen} session={session} onRefresh={() => refreshSession(registerId)} guardWithStepUp={guardWithStepUp} />}
      <StepUpDialog open={!!stepUp} onOpenChange={(o) => !o && setStepUp(null)} actions={stepUp?.actions || []} onAuthorized={(tok) => { stepUp?.onToken(tok); setStepUp(null); }} />

      {/* override dialog */}
      <OverrideDialog line={overrideLine} onOpenChange={(o) => !o && setOverrideLine(null)} onApply={applyOverride} />

      {/* held sales */}
      <Dialog open={heldOpen} onOpenChange={setHeldOpen}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-100" data-testid="pos-held-dialog">
          <DialogHeader><DialogTitle>{t("pos.held_sales")}</DialogTitle></DialogHeader>
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {held.length === 0 ? <p className="py-6 text-center text-sm text-slate-500">{t("pos.no_held")}</p> :
              held.map((s) => (
                <button key={s.id} onClick={() => resumeSale(s)} className="flex w-full items-center justify-between rounded border p-3 text-left hover:bg-slate-100 dark:hover:bg-slate-800" data-testid={`pos-held-${s.id}`}>
                  <span>{s.hold_name || t("pos.cart")} · {s.lines.length} {t("pos.items")}</span>
                  <span className="font-bold tabular-nums">{fmtCurrency(s.gross_total)}</span>
                </button>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* recent sales */}
      <Dialog open={recentOpen} onOpenChange={setRecentOpen}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-100" data-testid="pos-recent-dialog">
          <DialogHeader><DialogTitle>{t("pos.recent_sales")}</DialogTitle></DialogHeader>
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {recent.length === 0 ? <p className="py-6 text-center text-sm text-slate-500">{t("pos.no_recent")}</p> :
              recent.map((s) => (
                <div key={s.id} className="flex items-center justify-between rounded border p-3" data-testid={`pos-recent-${s.id}`}>
                  <span className="text-sm">{s.receipt_number} · {fmtCurrency(s.gross_total)}</span>
                  <Button variant="outline" size="sm" onClick={() => startReturn(s)} data-testid={`pos-recent-return-${s.id}`}><RotateCcw className="mr-1 h-4 w-4" />{t("pos.return")}</Button>
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* close session (Z report) */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-100" data-testid="pos-close-dialog">
          <DialogHeader><DialogTitle>{t("pos.close_title")}</DialogTitle></DialogHeader>
          {session && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded border border-slate-700 bg-slate-800 p-2 text-slate-100"><div className="text-slate-400">{t("pos.opening_float")}</div><b className="tabular-nums">{fmtCurrency(session.opening_float)}</b></div>
                <div className="rounded border border-slate-700 bg-slate-800 p-2 text-slate-100"><div className="text-slate-400">{t("pos.cash_sales")}</div><b className="tabular-nums">{fmtCurrency(session.cash_sales)}</b></div>
                <div className="col-span-2 rounded border border-slate-700 bg-slate-800 p-2 text-slate-100"><div className="text-slate-400">{t("pos.expected_cash")}</div><b className="tabular-nums" data-testid="pos-expected-cash">{fmtCurrency(session.expected_cash)}</b></div>
              </div>
              <div className="space-y-1.5">
                <Label>{t("pos.counted_cash")}</Label>
                <Input type="number" step="0.01" value={countedCash} onChange={(e) => setCountedCash(e.target.value)} className="tabular-nums" data-testid="pos-counted-cash" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("pos.variance_reason")}</Label>
                <Input value={varReason} onChange={(e) => setVarReason(e.target.value)} data-testid="pos-variance-reason" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)} data-testid="pos-close-cancel">{t("common.cancel")}</Button>
            <Button onClick={confirmClose} data-testid="pos-close-confirm">{t("pos.confirm_close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {locked && <LockOverlay cashier={cashier} onUnlock={(data) => { setCashier(data); setLocked(false); }} onLogout={cashierLogout} />}
    </div>
  );
}

/* ---------------- Override dialog ---------------- */
function OverrideDialog({ line, onOpenChange, onApply }) {
  const { t } = useI18n();
  const [price, setPrice] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => { if (line) { setPrice(String(line.unit_price)); setReason(""); } }, [line]);
  if (!line) return null;
  return (
    <Dialog open={!!line} onOpenChange={onOpenChange}>
      <DialogContent className="border-slate-800 bg-slate-900 text-slate-100" data-testid="pos-override-dialog">
        <DialogHeader>
          <DialogTitle>{t("pos.override")}</DialogTitle>
          <DialogDescription>{line.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("pos.override_price")}</Label>
            <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="tabular-nums" data-testid="pos-override-price" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("pos.override_reason")} *</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="pos-override-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pos-override-cancel">{t("common.cancel")}</Button>
          <Button onClick={() => onApply(line, price, reason)} disabled={!reason} data-testid="pos-override-apply">{t("pos.apply")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
